
import config from '../config/config'
import { canUseDOM } from 'exenv'
const buildtimestamp = require('../buildtimestamp.json')

// On client-side 'maxXyZToLog' applies to one tab so we limit errors heavily.
// On server-side 'maxXyZToLog' applies to lifetime of the process.
let maxExceptionsToLog = canUseDOM  ? 5 : 200
let maxWarningsToLog = canUseDOM ? 5 : 200
let sentry = null


async function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}


/**
 * Check if the user has given consent for Sentry tracking. Resolves only once
 * consent status is known.
 * @returns {Promise<boolean>} true if user has given consent for Sentry tracking
 */
async function isSentryConsented() {
  if (!canUseDOM) return true // Server-side always ok, no PII collected

  const SENTRY_VENDOR_ID = 33 // https://github.com/almamedia/alma-cmp-consents?tab=readme-ov-file#get-non-tcf-vendor-consents
  const ACCESS_DEVICE_INFO_PURPOSE = 1 // https://github.com/almamedia/alma-cmp-consents?tab=readme-ov-file#purposes
  const IMPROVE_PRODUCTS_PURPOSE = 10

  // Alma CMP is externally loaded, wait until it is available
  const timeout = Date.now() + 30_000
  while (typeof window?.ALMA?.cmpV2?.getPublisherConsents !== 'function') {
    if (Date.now() > timeout) return false
    await sleep(1000)
  }
  try {
    const consents = await window.ALMA.cmpV2.getPublisherConsents()
    const vendors = consents?.nonTCFVendors ?? {}
    const purposes = consents?.standard ?? {}

    return (
      vendors[SENTRY_VENDOR_ID] === true &&
      purposes[ACCESS_DEVICE_INFO_PURPOSE] === true &&
      purposes[IMPROVE_PRODUCTS_PURPOSE] === true
    )
  } catch (error) {
    return false
  }
}


/**
 * Init sentry. Sentry implementation must be provided as a parameter because client-side
 * and server-side have different implementations. Runs async because of consent checks.
 * @param {import('@sentry/react') | import('@sentry/node') | import('@sentry/browser')} sentryImpl
 * @returns {Promise<void>}
 */
export async function initSentry(sentryImpl) {
  const consent = await isSentryConsented()
  if (!consent || config.sentry.sampleRate == null || sentry != null) {
    return
  }

  // Sentry sampleRate applies per event. We turn it to per user (or per
  // browser tab to be more accurate) instead. Most users will not load
  // sentry at all, while some will submit all errors and warnings.
  if (canUseDOM && config.sentry.sampleRate < Math.random()) {
    return
  }

  sentry = sentryImpl

  /** @type {import('@sentry/browser').BrowserOptions} */
  const sentryConfig = Object.assign({}, config.sentry, {sampleRate: 1.0})
  if (canUseDOM) {
    sentryConfig.environment = window?.location?.hostname ?? 'unknown-client'
  } else {
    sentryConfig.environment = process?.env?.HOSTNAME ?? 'unknown-server'
  }
  sentryConfig.sendDefaultPii = false
  sentryConfig.release = buildtimestamp
  sentryConfig.allowUrls = [
    'ampparit.com/',
    'webpack-internal:',
  ]
  sentryConfig.denyUrls = [
    'permutive.app/',
    'adnxs.com/',
  ]
  sentryConfig.beforeSend = beforeSend
  sentry.init(sentryConfig)
  // TODO: Remove this at some point, just testing CMP compliance for now
  console.log('Sentry initialized') // eslint-disable-line no-console
}


export function logException(error, message) {
  if (error && sentry && maxExceptionsToLog-- > 0) {
    sentry.withScope(scope => {
      if (message) {
        scope.setTag('message', message)
      }
      if (error.name === 'AxiosError') {
        scope.setTag('axios.url', error.config?.url ?? '')
        scope.setTag('axios.code', error.code ?? '-')
        scope.setTag('axios.status', error.response?.status ?? '-')
        scope.setTag('axios.method', error.config?.method ?? '-')
      }
      sentry.captureException(error)
    })
  }
  console.error(error, message)
}


export function logWarning(message) {
  if (message && sentry && maxWarningsToLog-- > 0) {
    sentry.captureMessage(message)
  }
  console.warn(message)
}


export function logBreadcrumb(message, data) {
  if (sentry) {
    sentry.addBreadcrumb({message, data})
  }
}


/**
 * Stringify an error object for logging. This handles normal Errors as well as plain Objects,
 * CustomEvents, and anything else. Most Error and Custom Event properties are not enumerable,
 * meaning JSON.stringify() ignores them. We copy them to a new object so they are retained.
 * Some unknown useful properties may still end up missing, but this should be pretty good.
 * @param {any} error
 */
function stringifyException(error) {
  const type = error?.constructor?.name ?? typeof error
  const copy = JSON.parse(JSON.stringify(error ?? null)) // ?? protects against undefined
  const extraProps = {}
  if (copy && typeof copy === 'object') {
    // Error properties
    copy.message = error.message
    copy.stack = error.stack
    copy.name = error.name
    if (error.cause) {
      copy.cause = stringifyException(error.cause)
    }
    // CustomEvent properties
    copy.type = error.type
    copy.detail = error.detail
    // Non standard properties we have found via extraProps
    copy.sourceURL = error.sourceURL
    // Get names of any other non-enumerable properties that might be useful
    for (const key of Object.getOwnPropertyNames(error)) {
      if (key in copy) continue
      if (key === '__sentry_captured__') continue
      const value = error[key]
      // if (value == null) continue
      //if (typeof value === 'boolean') continue
      if (typeof value === 'function') continue
      if (typeof value === 'symbol') continue
      if (typeof value === 'number' && ['line', 'lineNumber', 'column', 'columnNumber'].includes(key)) continue
      if (typeof value === 'string' && ['fileName'].includes(key)) continue
      if (Array.isArray(value)) {
        extraProps[key] = `Array (length: ${value.length})`
      }
      else if (typeof value === 'object') {
        extraProps[key] = String(value)
      }
      else {
        extraProps[key] = typeof value
      }
    }
    // These are included in errors sometimes, but are quite useless
    if (error instanceof Error) {
      copy.line = copy.lineNumber = copy.column = copy.columnNumber = undefined
    }
  }
  if (Object.keys(extraProps).length > 0) {
    return JSON.stringify({ errorType: type, errorProps: copy, extraProps: extraProps })
  }
  return JSON.stringify({ errorType: type, errorProps: copy })
}


function beforeSend(event, hint) {
  const err = hint.originalException ?? hint.syntheticException
  const type = err?.constructor?.name?.trim() ?? typeof err
  const name = err?.name?.trim() ?? ''
  const msg = err?.message?.trim() ?? ''
  let stack = err?.stack?.trim() ?? ''
  if (stack === `${name}: ${msg}`) {
    stack = '' // If stack is this useless, normalize to empty
  }

  /**
   * Something was null or undefined, stack contains something we can grab on to
   */
  const isNullErrorFrom = (stackFragment) => {
    return name === 'TypeError' &&
      (msg.includes('null') || msg.includes('undefined')) &&
      stack.includes(stackFragment)
  }

  /**
   * "Failed to fetch (prebid-server.rubiconproject.com)" or
   * "NetworkError when attempting to fetch resource. (adx.adform.net)"
   * These often don't contain stack traces so we have to check the message
   */
  const isFetchErrorFrom = (domain) => {
    return name === 'TypeError' && (
      msg === `Failed to fetch (${domain})` ||
      msg === `NetworkError when attempting to fetch resource. (${domain})` ||
      (msg === 'Failed to fetch' && stack.includes(domain)) ||
      (msg === 'NetworkError when attempting to fetch resource.' && stack.includes(domain))
    )
  }

  // Filter 3rd party errors, and things that are unfixable (like your phone losing network for a while)
  if (
    // In javascript it is possible to throw null/undefined. Good luck debugging with no info at all.
    err == null

    // Randomly see this raw string thrown in Firefox
    || err === 'out of memory'

    // Permutive. Throws raw strings
    || (err === 'Failed to track Pageview. Network request failed')
    || (err === 'Failed to track Pageview. Network request timed out')
    || (err === 'Failed to track Pageview. A validation check carried out by the server did not succeed.')
    || (err === 'Failed to track AppNexusAdImpression. Network request failed')

    // Random network (?) errors. No idea where these come from. No stack trace so impossible to
    // figure out. Breadcrumbs indicate that ad requests being blocked is often involved.
    || (name === 'AbortError' && msg === 'The operation was aborted.' && !stack)
    || (name === 'TypeError' && msg === 'NetworkError when attempting to fetch resource.' && !stack)
    || (name === 'TypeError' && msg === 'Failed to fetch' && !stack)
    || (name === 'TypeError' && msg === 'Load failed' && !stack)
    || (name === 'Error' && msg === 'invalid origin' && !stack)

    // Errors relating to videos and/or fullscreen. We do neither so these are always ads
    || (name === 'TypeError' && msg === 'Permissions check failed' && !stack)
    || (name === 'ReferenceError' && msg === 'Can\'t find variable: closeFullScreen')

    // Something firefox has been spamming us with from few ad types. Related to animations I think
    || (name === 'NS_ERROR_NOT_INITIALIZED' && msg === '' && stack.includes('createContainerViewabilityObserver'))
    || (name === 'NS_ERROR_NOT_INITIALIZED' && msg === '' && stack.includes('ANOutstreamVideo.js'))
    || (name === 'NS_ERROR_NOT_INITIALIZED' && msg === '' && stack.includes('VPAIDAdapter'))
    || (name === 'NS_ERROR_NOT_INITIALIZED' && msg === '' && stack.includes('vpaid.js'))
    || (name === 'NS_ERROR_NOT_INITIALIZED' && msg === '' && stack.includes('GalleryGroup'))
    || (name === 'NS_ERROR_NOT_INITIALIZED' && msg === '' && stack.includes('CTicker'))
    || (name === 'NS_ERROR_NOT_INITIALIZED' && msg === '' && stack.includes('injectedScript'))

    // Errors where stack clearly shows that ad-systems are involved
    || isNullErrorFrom('.creative')
    || isNullErrorFrom('browser_notify_position')
    || isNullErrorFrom('cleanupPlacement')
    || isNullErrorFrom('CrossScreenBannerFormat')
    || isNullErrorFrom('frame.onload')
    || isNullErrorFrom('getViewportGeometry')
    || isNullErrorFrom('hideCertainAdElements')
    || isNullErrorFrom('hideIABFriendlyIframe')
    || isNullErrorFrom('IframePlacement')
    || isNullErrorFrom('InterscrollerFormat')
    || isNullErrorFrom('Placement.getContentWindow')
    || isNullErrorFrom('UnitViewabilityObserver')
    || isNullErrorFrom('UniversalInteractiveVideoFormat')
    || (name === 'Error' && msg === 'Root node not attached to document yet!' && stack.includes('IframePlacement'))
    || (name === 'Error' && msg === 'Invalid state transition request! (dismissed => dismissed)' && stack.includes('CrossScreenBannerFormat'))

    // Fetch errors from known external domains
    || isFetchErrorFrom('acdn.adnxs-simple.com')
    || isFetchErrorFrom('adsdk.bing.net')
    || isFetchErrorFrom('adx.adform.net')
    || isFetchErrorFrom('api.permutive.com')
    || isFetchErrorFrom('cdn.ats.alma-amr.net')
    || isFetchErrorFrom('cdn.jsdelivr.net')
    || isFetchErrorFrom('cdn.privacy-mgmt.com')
    || isFetchErrorFrom('europe.delivery.adnuntius.com')
    || isFetchErrorFrom('fastlane.rubiconproject.com')
    || isFetchErrorFrom('googleads.g.doubleclick.net')
    || isFetchErrorFrom('grid-bidder.criteo.com')
    || isFetchErrorFrom('hbopenbid.pubmatic.com')
    || isFetchErrorFrom('pagead2.googlesyndication.com')
    || isFetchErrorFrom('prebid-server-ams3.rubiconproject.com')
    || isFetchErrorFrom('prebid-server-fra2.rubiconproject.com')
    || isFetchErrorFrom('prebid-server.rubiconproject.com')
    || isFetchErrorFrom('securepubads.g.doubleclick.net')
    || isFetchErrorFrom('target.digitalaudience.io')
    || isFetchErrorFrom('www.googletagmanager.com')
    || isFetchErrorFrom('measure.lamp.avct.cloud')

    // Errors where all stack frames point to code injected to page HTML (ie. nothing really usable. But it's all ads)
    || (name === 'TypeError' && msg === 'null is not an object (evaluating \'this.placement.getContainer().getBoundingClientRect\')')
    || (name === 'TypeError' && msg === 'null is not an object (evaluating \'this.getContainer().ownerDocument\')')
    || (name === 'TypeError' && msg === 'null is not an object (evaluating \'this.root.ownerDocument\')')
    || (name === 'TypeError' && msg === 'undefined is not an object (evaluating \'t.creative\')')
    || (name === 'TypeError' && msg === 'Cannot read properties of null (reading \'document\')')
    || (name === 'TypeError' && msg === 'this._resizeCallback is not a function')
    || (name === 'Error' && msg === 'Can\'t access contentWindow of an iframe, skip placement initialization.')

    // These don't have usable stack but are all from https://target.digitalaudience.io/bakery/scripts/da.js
    || (name === 'TypeError' && msg === 'Cannot read property \'bake\' of undefined')
    || (name === 'TypeError' && msg === 'Cannot read properties of undefined (reading \'bake\')')
    || (name === 'TypeError' && msg === 'undefined is not an object (evaluating \'window.__da.bake\')')
    || (name === 'TypeError' && msg === 'window.__da is undefined')
    || (name === 'TypeError' && msg === 'can\'t access property "bake", window.__da is undefined')

    // No idea, stack not included. But we build our code so we don't have stupid syntax errors
    || (name === 'SyntaxError' && msg === 'Unexpected token \'else\'' && !stack)
    || (name === 'SyntaxError' && msg === 'Unexpected identifier \'http\'' && !stack)
    || (name === 'SyntaxError' && msg === 'Unexpected identifier \'https\'' && !stack)
    || (name === 'SyntaxError' && msg === 'No identifiers allowed directly after numeric literal' && !stack)
    || (name === 'SyntaxError' && msg === 'The string did not match the expected pattern.' && !stack)

    // No idea, this is all we get
    || (type === 'CustomEvent' && !msg && !stack && err.detail == null && err.type === 'unhandledrejection')

    // Network connection errors. These are in our code but we can't do anything about them
    || (name === 'AxiosError' && err.code === 'ETIMEDOUT')
    || (name === 'AxiosError' && err.code === 'ERR_NETWORK')
    || (name === 'AxiosError' && err.code === 'ECONNABORTED')

    // Some odd Android error that we don't have control over
    || (name === 'Error' && msg === 'Error invoking log: Java object is gone')

    // IOS/OSX WebView. Whatever these are we never call postMessage/sendMessage in our code
    || (name === 'SyntaxError' && msg === 'The string did not match the expected pattern.' && stack === 'postMessage@[native code]')
    || (name === 'Error' && msg === 'WKWebView API client did not respond to this postMessage' && !stack)
    || (name === 'Error' && msg === 'Invalid call to runtime.sendMessage(). Tab not found.' && !stack)

    // Alma's ad/cmp/etc scripts
    || (name === 'AbortError' && msg === 'The operation was aborted.' && stack.includes('https://cdn.ats.alma-amr.net/'))
    || (name === 'AbortError' && msg === 'Request signal is aborted' && stack.includes('https://cdn.ats.alma-amr.net/'))

    // Browser extensions or userscripts
    || (stack.includes('chrome-extension://'))
    || (err === 'Not implemented on this platform') // LastPass https://github.com/WordPress/openverse/issues/524
    || (type === 'Object' && msg === 'wallet must has at least one account') // Crypto wallets https://github.com/search?q=%22wallet+must+has+at+least+one+account%22&type=code
    || (type === 'Object' && err.code === 4900 && msg === 'The provider is disconnected from all chains.') // Crypto wallets https://eips.ethereum.org/EIPS/eip-1193#provider-errors
    || (type === 'Object' && err.code === 4001 && msg === 'User rejected the request.')                    // Crypto wallets https://eips.ethereum.org/EIPS/eip-1193#provider-errors

    // Odd GTM errors that are not ours to fix
    || (name === 'SecurityError' && msg === 'The operation is insecure.' && stack.includes('https://www.googletagmanager.com/'))
    || (name.startsWith('NS_ERROR_') && msg === '' && stack.includes('https://www.googletagmanager.com/'))

    // Ads trying to access things they are not supposed to?
    || (name === 'SecurityError' && msg === 'Failed to read a named property \'addEventListener\' from \'Window\': Blocked a frame with origin "https://www.ampparit.com" from accessing a cross-origin frame.')
    || (name === 'SecurityError' && msg === 'Blocked a restricted frame with origin "https://www.ampparit.com" from accessing another frame.')

    // Odd pattern of unhandled errors, that probably come from cdn.ats.alma-amr.net
    || (name === 'TypeError' && msg.match(/^Cannot read properties of undefined \(reading '\w{10}'\)$/) && stack.includes('at <anonymous>:'))
  ) {
    return null
  }

  // This is a bot masquerading as Chrome 66, no reason to log errors. Chrome 66 is from 2018 so we are not missing anything legit.
  if (canUseDOM && window?.navigator?.userAgent === 'Mozilla/5.0 (Windows NT 6.1; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/66.0.3359.117 Safari/537.36') {
    return null
  }

  // Sentry drops non-standard properties from exceptions so we save them as attachments
  hint.attachments ??= []
  if (hint.originalException) {
    hint.attachments.push({filename: 'originalException.json', data: stringifyException(hint.originalException), contentType: 'application/json'})
  }
  if (hint.syntheticException) {
    hint.attachments.push({filename: 'syntheticException.json', data: stringifyException(hint.syntheticException), contentType: 'application/json'})
  }

  return event
}
